'use client';

import './article-tabs.scss';

import { t } from 'i18next';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import {
  TabGroup,
  TabGroupProps,
} from '@sola/ark-core/molecules/tab-group/tab-group.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.js';

import { useTabGroupQueryParams } from '../../utils/query-params';
import { ArticleControls } from './article-controls';
import { ArticleListProps } from './article-list';
import { ArticleTabsEnum, ArticleTermList } from './article-term-list';

const getTabItems = (
  seoLevel: number,
  articleType: ArticleTypesEnum,
  limit?: number,
  articleData?: QueryResponsePublished,
): TabGroupProps => {
  const seoLevelTabFirst = seoLevel;
  const seoLevelTab = seoLevel === 1 ? 2 : seoLevel;
  const tabs = {
    title: 'Article Title',
    items: [
      {
        // todo: move to article.tabs
        title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.all}`),
        tabKey: ArticleTabsEnum.enum.all as ArticleTabsEnum,
        body: (
          <ArticleControls
            seoLevel={seoLevelTabFirst + 1}
            articleType={articleType}
            limit={limit || 12}
          />
        ),
        seoLevel: seoLevelTabFirst,
      },
    ],
  };

  const facetsThemes =
    articleData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.articleThemes.keyword',
    ) || [];
  const tabActiveThemes = facetsThemes.some(
    (facet) => facet?.values && facet.values.length > 3,
  );
  if (tabActiveThemes) {
    tabs.items.push({
      title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.theme}`),
      tabKey: ArticleTabsEnum.enum.theme,
      body: (
        <div className="py-roomy-sm">
          <ArticleTermList
            seoLevel={seoLevelTab + 1}
            tabGroup={ArticleTabsEnum.enum.theme}
            articleType={articleType}
            facets={facetsThemes}
          />
        </div>
      ),
      seoLevel: seoLevelTab,
    });
  }

  const facetsTopics =
    articleData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.articleTopics.keyword',
    ) || [];
  const tabActiveTopics = facetsTopics.some(
    (facet) => facet?.values && facet.values.length > 3,
  );
  if (tabActiveTopics) {
    tabs.items.push({
      title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.topic}`),
      tabKey: ArticleTabsEnum.enum.topic,
      body: (
        <div className="py-roomy-sm">
          <ArticleTermList
            seoLevel={seoLevelTab + 1}
            tabGroup={ArticleTabsEnum.enum.topic}
            articleType={articleType}
            facets={facetsTopics}
          />
        </div>
      ),
      seoLevel: seoLevelTab,
    });
  }

  const facetsCountry =
    articleData?.facets.filter(
      (facet) => facet?.field === 'taxonomy.programCountry.keyword',
    ) || [];
  const tabActiveCountry = facetsCountry.some(
    (facet) => facet?.values && facet.values.length > 3,
  );
  if (tabActiveCountry) {
    tabs.items.push({
      title: t(`common:skippy.tabs.${ArticleTabsEnum.enum.country}`),
      tabKey: ArticleTabsEnum.enum.country,
      body: (
        <div className="py-roomy-sm">
          <ArticleTermList
            seoLevel={seoLevelTab + 1}
            tabGroup={ArticleTabsEnum.enum.country}
            articleType={articleType}
            facets={facetsCountry}
          />
        </div>
      ),
      seoLevel: seoLevelTab,
    });
  }

  // @TODO: bring back when content hub search is supported
  // tabs.items.push({
  //   title: t(`skippy.tabs.${ArticleTabsEnum.Enum.search}`),
  //   tabKey: ArticleTabsEnum.Enum.search,
  //   body: (
  //     <div className="py-roomy-sm">
  //       <SearchBar
  //         title={t('skippy.article.searchContentHub')}
  //         placeholder={t('skippy.article.searchPlaceholder')}
  //         seoLevel={seoLevelTab + 1}
  //       />
  //     </div>
  //   ),
  //   icon: 'search-sm',
  //   seoLevel: seoLevelTab,
  // });

  return tabs;
};

export const ArticleTabs: React.FC<ArticleListProps> = ({
  seoLevel = 2,
  articleType = ArticleTypesEnum.enum.blogPost,
  limit,
}) => {
  const { tabGroupFilters, setTabGroupFilters } = useTabGroupQueryParams();

  // TEMP - just trying to more centrally getting facet info
  // @TODO Move the logic of molecules/article-list to a more parent logic-filled part organism (/organisms/article-controls)
  // handle the tab variant based on the props
  const articleFacetQuery = useListArticlesQuery({
    perPage: 1,
    filters: JSON.stringify({
      'taxonomy.articleType.keyword': articleType || undefined,
    }),
  });
  const { data: articleData } = articleFacetQuery;

  const tabItems = getTabItems(seoLevel, articleType, limit, articleData);

  if (tabItems.items?.length === 1) {
    return tabItems.items[0].body;
  }

  return (
    <TabGroup
      {...tabItems}
      activeValue={tabGroupFilters}
      onChange={(v: string) => {
        setTabGroupFilters(v as ArticleTabsEnum);
      }}
    />
  );
};

/**
 * Skeleton component for ArticleTabs.
 * Matches the dimensions and layout of the ArticleTabs component.
 */
export const ArticleTabsSkeleton = () => {
  const tabSkeletonItems = ['all', 'theme', 'topic', 'country'] as const;
  const articleCardSkeletonKeys = Array.from(
    { length: 8 },
    (_, idx) => `article-card-skeleton-${idx + 1}`,
  );

  return (
    <div className="TabGroup grid gap-comfy-lg">
      {/* Tab navigation skeleton */}
      <div className="flex gap-snug-sm overflow-x-auto pb-snug-sm">
        {tabSkeletonItems.map((tab) => (
          <Skeleton
            key={`article-tab-${tab}`}
            height={40}
            width={110}
            borderRadius="0.5rem"
            style={{ minWidth: '6.5rem' }}
          />
        ))}
      </div>

      {/* Tab content skeleton */}
      <div className="grid gap-comfy-xl">
        {/* ArticleFilter skeleton */}
        <div className="ArticleFilter">
          <div className="ArticleFilter__container flex flex-col md:flex-row md:justify-between md:align-center gap-comfy-lg">
            <div className="flex flex-col gap-snug-xs">
              <Skeleton height={18} width={140} />
              <Skeleton height={14} width={100} className="hidden md:block" />
            </div>
            <div className="flex gap-comfy-sm w-full md:w-auto">
              <Skeleton
                height={40}
                width="100%"
                borderRadius="0.5rem"
                className="md:w-60"
              />
              <Skeleton height={40} width={80} borderRadius="0.5rem" />
            </div>
          </div>
        </div>

        {/* Article cards grid skeleton */}
        <div className="grid gap-comfy-sm @md:gap-comfy-lg grid-cols-1 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4">
          {articleCardSkeletonKeys.map((key) => (
            <div key={key} className="flex flex-col gap-comfy-sm">
              {/* Image skeleton */}
              <Skeleton
                width="100%"
                height="auto"
                style={{ aspectRatio: '16/9' }}
                borderRadius="0.5rem"
              />
              {/* Eyebrow skeleton */}
              <Skeleton height={16} width="40%" />
              {/* Title skeleton */}
              <div className="flex flex-col gap-snug-xs">
                <Skeleton height={22} width="100%" />
                <Skeleton height={22} width="85%" />
              </div>
              {/* Summary skeleton */}
              <div className="flex flex-col gap-snug-xs">
                <Skeleton height={16} width="100%" />
                <Skeleton height={16} width="95%" />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination skeleton */}
        <div className="flex justify-center mt-comfy-lg pt-comfy-lg border-t border-neutral-mild">
          <div className="flex gap-snug-sm">
            <Skeleton height={48} width={48} borderRadius="0.5rem" />
            <Skeleton height={48} width={96} borderRadius="0.5rem" />
            <Skeleton height={48} width={48} borderRadius="0.5rem" />
          </div>
        </div>
      </div>
    </div>
  );
};
